import { eventFilters } from '@/features/publicsite/events/eventFilters';

/**
 * The page the calendar's URL asks for, counted from 1.
 *
 * The URL counts from 1 and the endpoint from 0, and this page keeps the two apart on purpose: a
 * visitor who edits `?page=` in the address bar, or reads it in a shared link, is reading the number
 * printed on the pager. (/stiri put the endpoint's own 0-based number in the URL, so its `?page=1`
 * is the second page — not a precedent to copy into a second public listing.) Anything that is not
 * a page number — `?page=abc`, `?page=0`, `?page=-3` — is the first page rather than an error.
 */
export function calendarPage(params: URLSearchParams): number {
  const asked = Number(params.get('page'));
  return Number.isInteger(asked) && asked > 1 ? asked : 1;
}

/**
 * The address of another page of the same filtered calendar.
 *
 * The filters come across, so paging a narrowed calendar does not quietly widen it again, and page
 * one is written without `page=` — one address for the first page rather than two, which is also
 * the address the page had before it paged at all.
 */
export function eventsPageHref(params: URLSearchParams, page: number): string {
  const next = new URLSearchParams(eventFilters(params));
  if (page > 1) next.set('page', String(page));
  const qs = next.toString();
  return qs ? `/evenimente?${qs}` : '/evenimente';
}

/**
 * The page to send a reader to when the one they asked for is past the end, or `null` when the page
 * they asked for is a real one.
 *
 * A link to page 4 outlives the events that made four pages: published events are unpublished and
 * the calendar shrinks under a shared address. The answer is the last page that does exist, not the
 * empty list the endpoint truthfully returns for page 4 of 2. A calendar that is empty for its
 * filters has no last page to go to and keeps its own address, so the reader sees which period they
 * asked for and can clear it.
 */
export function outOfRangePage(requested: number, totalPages: number): number | null {
  return totalPages > 0 && requested > totalPages ? totalPages : null;
}
